import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Siparisler = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete, canSeePrices } = useAuth();
  const [siparisler, setSiparisler] = useState([]);
  const [musteriler, setMusteriler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [durumFilter, setDurumFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [siparislerRes, musterilerRes] = await Promise.all([
        axios.get(`${API}/siparisler`),
        axios.get(`${API}/musteriler`)
      ]);
      
      // En yeni sipariş en üstte olacak şekilde sırala
      const sortedSiparisler = siparislerRes.data.sort((a, b) => {
        const dateA = new Date(a.created_at || a.siparis_tarihi || 0);
        const dateB = new Date(b.created_at || b.siparis_tarihi || 0);
        return dateB - dateA;
      });
      
      setSiparisler(sortedSiparisler);
      setMusteriler(musterilerRes.data);
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu siparişi silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`${API}/siparisler/${id}`);
        fetchData();
      } catch (error) {
        console.error('Sipariş silinirken hata:', error);
        alert('Sipariş silinemedi');
      }
    }
  };

  const getMusteriAdi = (musteri_id) => {
    const musteri = musteriler.find(m => m.id === musteri_id);
    return musteri ? musteri.ad : '-';
  };

  const getDurumBadge = (durum) => {
    const durumMap = {
      'Beklemede': 'waiting',
      'Üretimde': 'production',
      'Boyamada': 'painting',
      'Montajda': 'assembly',
      'Teslim Edildi': 'delivered'
    };
    return durumMap[durum] || 'waiting';
  };

  // Arama ve durum filtresi
  const filteredSiparisler = siparisler.filter(siparis => {
    const searchLower = searchTerm.toLowerCase();
    const musteriAdi = getMusteriAdi(siparis.musteri_id).toLowerCase();
    
    const searchMatch = (
      siparis.siparis_no?.toLowerCase().includes(searchLower) ||
      musteriAdi.includes(searchLower) ||
      siparis.durum?.toLowerCase().includes(searchLower)
    );
    
    const durumMatch = !durumFilter || siparis.durum === durumFilter;
    
    return searchMatch && durumMatch;
  });

  if (loading) {
    return <div className="loading" data-testid="loading">Yükleniyor...</div>;
  }

  return (
    <div data-testid="orders-page">
      <div className="page-header">
        <h1 className="page-title">Siparişler</h1>
        <p className="page-subtitle">Sipariş kayıtlarını görüntüleyin ve yönetin</p>
      </div>

      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Sipariş no, müşteri adı veya durum ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-input"
          style={{ flex: 1, minWidth: '300px', maxWidth: '400px' }}
        />
        <select
          value={durumFilter}
          onChange={(e) => setDurumFilter(e.target.value)}
          className="form-select"
          style={{ minWidth: '150px' }}
        >
          <option value="">Tüm Durumlar</option>
          <option value="Beklemede">Beklemede</option>
          <option value="Üretimde">Üretimde</option>
          <option value="Boyamada">Boyamada</option>
          <option value="Montajda">Montajda</option>
          <option value="Teslim Edildi">Teslim Edildi</option>
        </select>
        {canEdit() && (
          <button className="btn btn-primary" onClick={() => navigate('/siparisler/yeni')} data-testid="add-order-btn">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Yeni Sipariş
          </button>
        )}
      </div>

      {filteredSiparisler.length === 0 ? (
        <div className="empty-state" data-testid="empty-state">
          {searchTerm ? (
            <>
              <h3>Arama sonucu bulunamadı</h3>
              <p>"{searchTerm}" ile eşleşen sipariş kaydı yok</p>
            </>
          ) : (
            <>
              <h3>Henüz sipariş kaydı yok</h3>
              <p>Yeni sipariş eklemek için yukarıdaki butona tıklayın</p>
            </>
          )}
        </div>
      ) : (
        <div className="card">
          <div style={{ marginBottom: '12px', color: '#666', fontSize: '14px' }}>
            {searchTerm && `${filteredSiparisler.length} sonuç bulundu`}
          </div>
          <table className="table" data-testid="orders-table">
            <thead>
              <tr>
                <th>Sipariş No</th>
                <th>Müşteri</th>
                {canSeePrices() && <th>Tutar</th>}
                <th>Durum</th>
                <th>Termin Tarihi</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredSiparisler.map(siparis => (
                <tr key={siparis.id} data-testid={`order-row-${siparis.id}`}>
                  <td style={{ fontWeight: 600 }}>{siparis.siparis_no}</td>
                  <td>{getMusteriAdi(siparis.musteri_id)}</td>
                  {canSeePrices() && <td>{siparis.tutar.toLocaleString('tr-TR')} {siparis.para_birimi}</td>}
                  <td>
                    <span className={`badge ${getDurumBadge(siparis.durum)}`}>
                      {siparis.durum}
                    </span>
                  </td>
                  <td>{siparis.termin_tarihi ? new Date(siparis.termin_tarihi).toLocaleDateString('tr-TR') : '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="icon-btn" onClick={() => navigate(`/siparisler/${siparis.id}`)} data-testid={`view-order-${siparis.id}`} title="Sipariş Detay">
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      {canDelete() && (
                        <button className="icon-btn danger" onClick={() => handleDelete(siparis.id)} data-testid={`delete-order-${siparis.id}`}>
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Siparisler;